import { useEffect, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { SignInForm } from './SignInForm'
import { SignUpForm } from './SignUpForm'
import { EmailVerificationForm } from './EmailVerificationForm'
import { ForgotPasswordForm } from './ForgotPasswordForm'
import { ResetPasswordForm } from './ResetPasswordForm'
import { NewPasswordForm } from './NewPasswordForm'
import { useCustomAuth } from '@/hooks/useCustomAuth'
import { useAmplify } from '@/contexts/amplify-context'
import { useTenant } from '@/lib/tenant/tenant-provider'
import { useAuth } from '@/hooks/useAuth'

interface AuthContainerProps {
  initialState?: 'signIn' | 'signUp'
  redirectTo?: string
}

export function AuthContainer({ initialState = 'signIn', redirectTo = '/' }: AuthContainerProps) {
  const authHook = useCustomAuth()
  const { authState, changeState, handleSignIn, handleSignUp, handleConfirmSignUp, handleResendCode, handleForgotPassword, handleResetPassword, handleConfirmSignInWithNewPassword } = authHook
  const { isConfigured, isLoading: amplifyLoading, error: amplifyError } = useAmplify()
  const { tenant } = useTenant()
  const { refreshUser } = useAuth()

  const navigate = useNavigate()
  const hasRedirectedRef = useRef(false)
  const hasSetInitialStateRef = useRef(false)

  // Set initial state only ONCE when component first mounts and Amplify is configured
  useEffect(() => {
    if (isConfigured && !hasSetInitialStateRef.current && authState.currentState !== 'authenticated' && !authState.isLoading) {
      hasSetInitialStateRef.current = true
      changeState(initialState)
    }
  }, [isConfigured, initialState, authState.currentState, authState.isLoading, changeState])

  // Handle successful authentication - fetch real user profile and redirect
  useEffect(() => {
    if (authState.currentState === 'authenticated' && !hasRedirectedRef.current && authState.user) {
      hasRedirectedRef.current = true
      
      const getUserProfileAndRedirect = async () => {
        try {
          // Clean any stale session data
          localStorage.removeItem('user-profile')
          
          // Fetch the real profile from /users/me (includes employee data, real role, etc.)
          await refreshUser()
          
          // Navigate once we have the real profile
          navigate({ to: redirectTo })
        } catch (error) {
          console.error('❌ Error fetching user profile:', error)
          // Clean up and redirect anyway - app can handle profile loading
          localStorage.removeItem('user-profile')
          navigate({ to: redirectTo })
        }
      }
      
      getUserProfileAndRedirect()
    }
  }, [authState.currentState, authState.user, redirectTo, navigate, refreshUser])

  const handleAuthSuccess = () => {
    // The useEffect will handle the redirect when authState becomes 'authenticated'
  }

  // Show loading while Amplify is configuring or checking existing auth
  if (amplifyLoading || (isConfigured && authState.isLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-dark" data-testid="auth-loading-container">
        <div className="text-center" data-testid="auth-loading-content">
          <div className="w-16 h-16 rounded-monumenta bg-gradient-to-br from-brand-primary to-brand-accent-600 flex items-center justify-center mx-auto mb-4 animate-pulse" data-testid="auth-loading-spinner">
            <div className="w-8 h-8 rounded-monumenta bg-white/30" />
          </div>
          <p className="text-brand-gray font-body" data-testid="auth-loading-message">
            {amplifyLoading ? 'Carregando configuração...' : 'Verificando autenticação...'}
          </p>
        </div>
      </div>
    )
  }

  // Show error if Amplify configuration failed
  if (amplifyError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-dark" data-testid="auth-error-container">
        <div className="text-center max-w-md mx-auto p-6" data-testid="auth-error-content">
          <div className="w-16 h-16 rounded-monumenta bg-error/20 flex items-center justify-center mx-auto mb-4" data-testid="auth-error-icon">
            <span className="text-2xl">⚠️</span>
          </div>
          <h2 className="text-xl font-display font-semibold mb-2 text-white" data-testid="auth-error-title">Erro de configuração</h2>
          <p className="text-error text-sm font-body" data-testid="auth-error-message">{amplifyError}</p>
        </div>
      </div>
    )
  }

  // Only render auth forms when Amplify is properly configured
  if (!isConfigured) {
    return null
  }

  // Debug: Log current auth state
  console.log('🔍 AuthContainer - Current State:', authState.currentState)
  console.log('🔍 AuthContainer - Full authState:', authState)

  // Render appropriate auth form based on current state
  // Using key prop to force React to unmount/remount when state changes
  const renderForm = () => {
    switch (authState.currentState) {
    case 'signIn':
      return <SignInForm
        key="signin-form"
        onSuccess={handleAuthSuccess}
        authState={authState}
        handleSignIn={handleSignIn}
        changeState={changeState}
        userPoolConfig={tenant?.config?.userPool}
      />

    case 'signUp':
      return <SignUpForm
        key="signup-form"
        onSuccess={handleAuthSuccess}
        authState={authState}
        handleSignUp={handleSignUp}
        changeState={changeState}
        userPoolConfig={tenant?.config?.userPool}
      />

    case 'confirmSignUp':
      return <EmailVerificationForm
        key="email-verification-form"
        onSuccess={handleAuthSuccess}
        authState={authState}
        handleConfirmSignUp={handleConfirmSignUp}
        handleResendCode={handleResendCode}
        changeState={changeState}
      />

    case 'forgotPassword':
      return <ForgotPasswordForm
        key="forgot-password-form"
        authState={authState}
        handleForgotPassword={handleForgotPassword}
        changeState={changeState}
      />

    case 'confirmResetPassword':
      return <ResetPasswordForm
        key="reset-password-form"
        authState={authState}
        handleResetPassword={handleResetPassword}
        changeState={changeState}
      />

    case 'confirmSignInWithNewPassword':
      return <NewPasswordForm
        key="new-password-form"
        authState={authState}
        handleConfirmSignInWithNewPassword={handleConfirmSignInWithNewPassword}
        changeState={changeState}
      />

    case 'authenticated':
      // Redirect is handled by useEffect above
      return (
        <div className="min-h-screen flex items-center justify-center bg-brand-dark" data-testid="auth-authenticated-container">
          <div className="text-center" data-testid="auth-authenticated-content">
            <div className="w-16 h-16 rounded-monumenta bg-gradient-to-br from-brand-primary to-brand-accent-600 flex items-center justify-center mx-auto mb-4" data-testid="auth-success-icon">
              <span className="text-2xl">✅</span>
            </div>
            <p className="text-brand-gray font-body" data-testid="auth-authenticated-message">Carregando...</p>
          </div>
        </div>
      )

    default:
      return <SignInForm
        key="signin-form-default"
        onSuccess={handleAuthSuccess}
        authState={authState}
        handleSignIn={handleSignIn}
        changeState={changeState}
        userPoolConfig={tenant?.config?.userPool}
      />
    }
  }

  return <div key={authState.currentState}>{renderForm()}</div>
}